import { picoapp } from 'picoapp'
import focusTrap from 'Assets/js/lib/focus-trap'
import Carousel from 'Assets/js/lib/carousel'
import { CARTSTATES, THEME } from 'Assets/js/lib/constants'
import { scrollToHandler } from 'Assets/js/lib/smoothscroll'

import {
  instantSearch,
  instantSearchQuery,
} from 'Assets/js/globals/instant-search'
import {
  productCardAction,
  productCardQuickShop,
  productCardSwatches,
} from 'Assets/js/globals/product-card'

const globalComponents = {
  accordion: SAXX.Init.Component((node) => {
    const button = node.querySelector('.accordion__button')
    const panel = node.querySelector('.accordion__panel')
    const siblings = () => {
      // Setup siblings array and get the first sibling
      const siblings = []
      let sibling = node.parentNode.firstChild

      // Loop through each sibling and push to the array
      while (sibling) {
        if (sibling.nodeType === 1 && sibling !== node) {
          siblings.push(sibling)
        }
        sibling = sibling.nextSibling
      }

      return siblings
    }

    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        if (node.getAttribute('data-close-siblings')) {
          siblings().forEach((e, i) => {
            const btn = e.querySelector('.accordion__button')
            if (btn) {
              const thisPanel = e.querySelector('.accordion__panel')
              const isExpanded = btn.getAttribute('aria-expanded')

              if (isExpanded) {
                btn.setAttribute('aria-expanded', false)
                thisPanel.hidden = true
              }
            }
          })
        }

        const isExpanded =
          button.getAttribute('aria-expanded') === 'true' || false

        button.setAttribute('aria-expanded', `${!isExpanded}`)
        panel.hidden = isExpanded
      })
    }
  }),
  carousel: SAXX.Init.Component((node) => {
    new Carousel(node)
  }),
  deferredMedia: SAXX.Init.Component((node) => {
    const loadAndPlayVideo = () => {
      const content = document.createElement('div')
      content.appendChild(
        node
          .querySelector('template')
          .content.firstElementChild.cloneNode(true),
      )

      const deferredElement = node.appendChild(
        content.querySelector('video, model-viewer, iframe'),
      )
      node.classList.add('loaded')

      if (
        deferredElement.nodeName === 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        deferredElement.play()
      }
    }

    node
      .querySelector('[id^="Deferred-Media--Poster-"]')
      .addEventListener('click', loadAndPlayVideo, { once: true })
  }),
  drawer: SAXX.Init.Component((node, ctx) => {
    const { firstNode } = focusTrap(node)
    const nodeId = node.getAttribute('data-drawer-id')

    node.querySelectorAll('[data-drawer-close]').forEach((ele) => {
      ele.addEventListener('click', (e) => {
        e.preventDefault()
        ctx.emit(THEME.drawerControlInteraction)
      })
    })

    ctx.on(
      [THEME.drawerControlInteraction, THEME.modalControlInteraction],
      (state, details) => {
        if (!details?.controlElement || nodeId !== details?.controlElement) {
          node.toggleAttribute('inert', true)
          node.setAttribute('aria-expanded', false)

          state.restoreFocus?.focus({ preventScroll: true })
          return
        }

        const isAlreadyOpen = !!JSON.parse(node.getAttribute('aria-expanded'))
        const shouldFocus = !JSON.parse(node.getAttribute('aria-expanded'))

        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)

        if (shouldFocus && !isAlreadyOpen) {
          ctx.hydrate({ restoreFocus: document.activeElement })
          firstNode.focus({ preventScroll: true })
        }
      },
    )

    ctx.on(THEME.customizerSelectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)
      }
    })

    ctx.on(THEME.customizerDeselectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', true)
        node.setAttribute('aria-expanded', false)
      }
    })

    node.addEventListener('keydown', (e) => {
      if (JSON.parse(node.getAttribute('aria-expanded')) === false) return

      const isEscPressed = e.key === 'Escape' || e.keyCode === 27
      if (!isEscPressed) return

      ctx.emit(THEME.drawerControlInteraction)
    })
  }),
  formValidation: SAXX.Init.Component((node) => {
    const formElements = Array.from(node.elements)
    const setErrorState = (ele) => {
      if (ele.type === 'submit') return

      Array.from(ele.parentNode.querySelectorAll('.text-help')).forEach(
        (message) => message.parentNode.removeChild(message),
      )

      if (ele.validity.valid) {
        ele.parentNode.classList.remove('has-danger')
        return
      }

      ele.parentNode.classList.add('has-danger')

      let message = 'is required.'
      if (ele.validity.typeMismatch || ele.validity.patternMismatch) {
        message = 'is not properly formatted.'
      }

      const name = ele.getAttribute('data-label')
      ele.parentNode.insertAdjacentHTML(
        'beforeend',
        `<small class="text-help">'${name}' ${message}</small>`,
      )
    }

    node
      .querySelectorAll('button[type="submit"],input[type="submit"]')
      .forEach((ele) =>
        ele.addEventListener('click', (e) => {
          if (!node.checkValidity()) {
            e.preventDefault()

            formElements.forEach(setErrorState)
          }
        }),
      )

    formElements.forEach((ele) => {
      ele.addEventListener('input', () => setErrorState(ele))
      ele.addEventListener('blur', () => setErrorState(ele))
      ele.addEventListener('change', () => setErrorState(ele))
    })
  }),
  inputSwitch: SAXX.Init.Component((node) => {
    node.addEventListener('change', () => {
      const id = node.getAttribute('data-target')
      const targets = Array.from(
        document.querySelectorAll(`[data-switch='${id}']`),
      )
      targets.forEach((target) => {
        target.classList.toggle('active', node.checked)
      })
    })
  }),
  modal: SAXX.Init.Component((node, ctx) => {
    const { firstNode } = focusTrap(node)
    const nodeId = node.getAttribute('data-modal-id')

    node.querySelectorAll('[data-modal-close]').forEach((ele) => {
      ele.addEventListener('click', (e) => {
        e.preventDefault()
        ctx.emit(THEME.modalControlInteraction)
      })
    })

    ctx.on(
      [THEME.drawerControlInteraction, THEME.modalControlInteraction],
      (state, details) => {
        if (!details?.controlElement || nodeId !== details?.controlElement) {
          node.toggleAttribute('inert', true)
          node.setAttribute('aria-expanded', false)

          state.restoreFocus?.focus({ preventScroll: true })
          return
        }

        const isAlreadyOpen = !!JSON.parse(node.getAttribute('aria-expanded'))
        const shouldFocus = !JSON.parse(node.getAttribute('aria-expanded'))

        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)

        if (shouldFocus && !isAlreadyOpen) {
          ctx.hydrate({ restoreFocus: document.activeElement })
          firstNode.focus({ preventScroll: true })
        }
      },
    )

    ctx.on(THEME.customizerSelectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)
      }
    })

    ctx.on(THEME.customizerDeselectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', true)
        node.setAttribute('aria-expanded', false)
      }
    })

    node.addEventListener('keydown', (e) => {
      if (JSON.parse(node.getAttribute('aria-expanded')) === false) return

      const isEscPressed = e.key === 'Escape' || e.keyCode === 27
      if (!isEscPressed) return

      ctx.emit(THEME.modalControlInteraction)
    })
  }),
  nostoCarousel: SAXX.Init.Component((node) => {
    const carouselChild = node.querySelector('.carousel--viewport').children[0]

    new Carousel(node, {
      cell:
        customElements.get(carouselChild.tagName.toLowerCase()) === undefined ?
          '.product-card'
        : carouselChild.tagName.toLowerCase(),
    })
  }),
  tabs: SAXX.Init.Component((node) => {
    /* This is a modified version of the W3C accessible tab interface scripting found at
     * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
     */

    const tabs = Array.from(node.querySelectorAll('[role=tab]'))
    const tabPanels = []
    let firstTab
    let lastTab

    const setSelectedTab = (currentTab) => {
      tabs.forEach((tab, i) => {
        if (currentTab === tab) {
          tab.setAttribute('aria-selected', 'true')
          tab.removeAttribute('tabindex')
          tab.classList.add('tab__active')
          tab.focus()
          tabPanels[i].classList.add('tab-panel__active')
        } else {
          tab.setAttribute('aria-selected', 'false')
          tab.tabIndex = -1
          tab.classList.remove('tab__active')
          tabPanels[i].classList.remove('tab-panel__active')
        }
      })
    }

    const setSelectedToPreviousTab = (currentTab) => {
      if (currentTab === firstTab) {
        return setSelectedTab(lastTab)
      }
      const index = tabs.indexOf(currentTab)
      setSelectedTab(tabs[index - 1])
    }

    const setSelectedToNextTab = (currentTab) => {
      if (currentTab === lastTab) {
        return setSelectedTab(firstTab)
      }
      const index = tabs.indexOf(currentTab)
      setSelectedTab(tabs[index + 1])
    }

    const onKeyDownEventListener = (event) => {
      const target = event.currentTarget
      let flag = false

      switch (event.key) {
        case 'ArrowLeft':
          setSelectedToPreviousTab(target)
          flag = true
          break
        case 'ArrowRight':
          setSelectedToNextTab(target)
          flag = true
          break
        case 'Home':
          setSelectedTab(firstTab)
          flag = true
          break
        case 'End':
          setSelectedTab(lastTab)
          flag = true
          break
      }

      if (flag) {
        event.stopPropagation()
        event.preventDefault()
      }
    }

    tabs.forEach((tab) => {
      tabPanels.push(document.getElementById(tab.getAttribute('aria-controls')))

      tab.addEventListener('keydown', onKeyDownEventListener)
      tab.addEventListener('click', (event) => {
        setSelectedTab(event.currentTarget)
      })

      if (!firstTab) {
        firstTab = tab
      }
      lastTab = tab
    })
  }),
}

const productCardComponents = {
  productCardAction,
  productCardQuickShop,
  productCardSwatches,
}

const instantSearchComponents = { instantSearch, instantSearchQuery }

SAXX.App = picoapp(SAXX.Init.Components, SAXX.Init.State)
SAXX.App.add(globalComponents)
SAXX.App.add(productCardComponents)
SAXX.App.add(instantSearchComponents)
SAXX.App.on('error', ({ error }) => {
  if (typeof window.Sentry === 'object') window.Sentry.captureException(error)
})

SAXX.App.on(CARTSTATES.UPDATED, async () => {
  document
    .querySelectorAll('[name="attributes[__nostoSessionId]"]')
    .forEach((input) => {
      input.value = window.localStorage.getItem('nosto_visit')
    })
})

SAXX.App.on('mount', () => {
  let i
  while (typeof (i = SAXX.Init.Callbacks.shift()) !== 'undefined') i()()

  document
    .querySelectorAll('[name="attributes[__nostoSessionId]"]')
    .forEach((input) => {
      input.value = window.localStorage.getItem('nosto_visit')
    })

  const handleDrawerTriggerClickEvent = (e) => {
    e.preventDefault()
    const controlElement = e.target.getAttribute('aria-controls')
    if (document.querySelector(`[data-drawer-id="${controlElement}"`)) {
      e.preventDefault()
      SAXX.App.emit(THEME.drawerControlInteraction, null, { controlElement })
    }
  }

  document.querySelectorAll('[data-drawer-trigger]').forEach((node) => {
    node.removeEventListener('click', handleDrawerTriggerClickEvent)
    node.addEventListener('click', handleDrawerTriggerClickEvent)
  })

  document.querySelectorAll('video[data-autoplay]').forEach((video) => {
    video.muted = true
    video.autoplay = true
    video.playsInline = true
    try {
      video.play().catch(console.warn)
    } catch (e) {}
  })

  const handleHashLinkClickEvent = (e) => {
    if (
      window.location.pathname === e.target.pathname &&
      e.target.hash &&
      e.target.hash.length > 0
    ) {
      e.preventDefault()
      window.location.hash = e.target.hash
      scrollToHandler(e.target.hash)
    }
  }

  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.removeEventListener('click', handleHashLinkClickEvent)
    link.addEventListener('click', handleHashLinkClickEvent)
  })
})

SAXX.App.on('nosto:postRender', () => {
  const state = SAXX.App.getState().nostoState ?? {
    filledElements: [],
    unFilledElements: [],
  }

  document.querySelectorAll('[data-placement-id]').forEach((placement) => {
    placement.classList.remove('loading')

    const placementId = placement.getAttribute('data-placement-id')

    placement.classList.toggle(
      'no-placement',
      !state.filledElements.some((ele) => ele === placementId) &&
        !placement.classList.contains('loaded'),
    )
    placement.classList.toggle(
      'loaded',
      state.filledElements.some((ele) => ele === placementId) ||
        placement.classList.contains('loaded'),
    )
  })

  if (
    !['CA', 'US'].includes(SAXX.Shop.Country) &&
    typeof window.Nosto === 'object'
  ) {
    window.Nosto.migrateToShopifyMarket({
      productSectionElement: '.nosto-rendered-card-element',
      productUrlElement: '.nosto-rendered-card-element--link',
      productTitleElement: '.nosto-rendered-card-element--title',
      productHandleAttribute: 'data-handle',
      priceElement: '.nosto-rendered-card-element--price',
      listPriceElement: '.nosto-rendered-card-element--list-price',
    })
  }

  SAXX.App.mount()
})

// One Time Executions
const offset = document.getElementById('site-navigation').clientHeight / 16
document
  .querySelector(':root')
  .style.setProperty('--sticky-top-offset', `${offset}rem`)

const geolizrLoader = setInterval(function () {
  if (typeof Geolizr === 'object') {
    clearInterval(geolizrLoader)
    Geolizr.getGeoData((location) => {
      SAXX.App.emit(THEME.userLocationIsSet, { location })
    })
  }
}, 100)

SAXX.App.mount()

// Document Event Listeners
document.addEventListener('shopify:section:load', (e) => {
  SAXX.App.mount()
  SAXX.App.emit(THEME.customizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:reorder', (e) => {
  SAXX.App.mount()
  SAXX.App.emit(THEME.customizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:select', (e) => {
  SAXX.App.emit(THEME.customizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:deselect', (e) => {
  SAXX.App.emit(THEME.customizerDeselectEvent, null, e.detail)
})

document.addEventListener('product:added', () => {
  SAXX.App.emit(CARTSTATES.EXTERNALUPDATE)

  SAXX.App.emit(THEME.drawerControlInteraction, null, {
    controlElement: 'slide-out-cart',
  })
})

document.addEventListener('custom-bundle:added', async (e) => {
  SAXX.App.emit(CARTSTATES.UPDATED, null, {
    cart: e.detail,
    addedLineItems: e.detail.items,
  })

  SAXX.App.emit(THEME.drawerControlInteraction, null, {
    controlElement: 'slide-out-cart',
  })
})
